import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  SelectInputField,
  // DateInputField,
  TextInputField,
  RadioItemListArrays,
  NumberInputField,
  CheckboxItemList,
  RichTextField,
  charCountLabel,
} from '../../../components/input';
import Button from '../../../components/Button.js';
import SectionTitle from '../../../components/SectionTitle.js';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ImageDropZone from './ImageDropZone';
import { useSelector } from 'react-redux';

const statusOptions = ['draft', 'active', 'closed'];
const minSessionOptions = ['no minimum', '1 hour', '2 hours', '3 hours', '4+ hours'];
const minWeekOptions = ['no minimum', 'less than 10 hours', 'more than 10 hours', 'more than 25 hours'];

const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: '0',
  },
  controls: {
    padding: '1rem 0',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  panelButtons: {
    width: '100%',
    padding: '1rem 0',
  },
  button: {
    marginLeft: '1rem',
  },
});

const ListingEditForm = ({listing, handleChange, clearAllLocations, copyProviderLocations, setImageFile, busy=false, back, save, children}) => {
  const classes = useStyles();
  const locationTaxonomy = useSelector(state => state.taxonomy.locations);
  const serviceTaxonomy = useSelector(state => state.taxonomy.services);
  const accessMethodTaxonomy = useSelector(state => state.taxonomy.accessMethods);
  const ageGroupTaxonomy = useSelector(state => state.taxonomy.ageGroups);
  return (
    <div className={classes.root}>
      <ExpansionPanel defaultExpanded>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-description"
          id="panel-description"
        >
          <SectionTitle title="Description - What is this listing for?" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <TextInputField
            {...charCountLabel('Title', listing.title, 255)}
            value={listing.title}
            onChange={e => handleChange(e.target.value, 'title')}
          />
          <TextInputField
            {...charCountLabel('Short Description', listing.short_description, 255)}
            value={listing.short_description}
            onChange={e => handleChange(e.target.value, 'short_description')}
          />
          <RichTextField
            inputValue={listing.full_description}
            allowEdit={true}
            label="Full Description (appears on listing page)"
            onChange={value => handleChange(value, 'full_description')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-image"
          id="panel-image"
        >
          <SectionTitle title="Listing Image" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <ImageDropZone
            instructions={[
              'If no image is provided for a listing, the organisation\'s profile image will be used for the listing.',
              'We recommend 1280x720 pixel JPG or PNG images for listing images.'
            ]}
            imageUrl={listing.image_path}
            setImageFile={setImageFile}
            clearImageUrl={() => handleChange(null, 'image_path')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-status"
          id="panel-status"
        >
          <SectionTitle title="Status - Is this listing publicly visible?" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <SelectInputField
            label="Status"
            value={listing.status}
            options={statusOptions}
            onChange={e => handleChange(e.target.value, 'status')}
          />
          {/*
          <DateInputField
            label="Active From"
            value={listing.active_from}
            onChange={(e) => handleChange(e.target.value, 'active_from')}
          />
          <DateInputField
            label="Active To"
            value={listing.active_to}
            onChange={(e) => handleChange(e.target.value, 'active_to')}
          />
          */}
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-service"
          id="panel-service"
        >
          <SectionTitle title="Service - What service is this for?" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <RadioItemListArrays
            items={serviceTaxonomy}
            value={listing.service}
            onChange={e => handleChange(e.target.value, 'service')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-locations"
          id="panel-locations"
        >
          <SectionTitle title="Service Locations - Where is this service available?" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <div className={classes.panelButtons}>
            <Button
              variant="contained"
              color="primary"
              onClick={copyProviderLocations}
              label="Copy Organisation Values"
              className={classes.button}
              pointer="none"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={clearAllLocations}
              label="Clear All"
              className={classes.button}
              pointer="none"
            />
          </div>
          {locationTaxonomy.map(item => {
            return (
              <CheckboxItemList
                items={item.children}
                checkedItems={listing.service_locations}
                header={item.name}
                key={item.id}
                onChange={(e) => handleChange(e.target.value, 'service_locations')}
              />
            );
          })}
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-availability"
          id="panel-availability"
        >
          <SectionTitle title="Availability - In what units is this is available?" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <SelectInputField
            label="Minimum Session/Shift Time"
            value={listing.minimum_session_time}
            options={minSessionOptions}
            onChange={(e) => handleChange(e.target.value, 'minimum_session_time')}
          />
          <SelectInputField
            label="Minimum Weekly Time"
            value={listing.minimum_weekly_time}
            options={minWeekOptions}
            onChange={(e) => handleChange(e.target.value, 'minimum_weekly_time')}
          />
          <NumberInputField
            label="Placements/Spots Available"
            value={listing.units_available}
            onChange={(e) => handleChange(e.target.value, 'units_available')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-age"
          id="panel-age"
        >
          <SectionTitle title="Age Groups - Who is this for?" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CheckboxItemList
            items={ageGroupTaxonomy}
            checkedItems={listing.age_groups}
            header={null}
            onChange={(e) => handleChange(e.target.value, 'age_groups')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-access"
          id="panel-access"
        >
          <SectionTitle title="Access Methods - How is it provided?" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CheckboxItemList
            items={accessMethodTaxonomy}
            checkedItems={listing.access_methods}
            header={null}
            onChange={(e) => handleChange(e.target.value, 'access_methods')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <div className={classes.controls}>
        {children}
        <Button
          variant="outlined"
          color="primary"
          onClick={back}
          label="Cancel"
          className={classes.button}
          pointer="none"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          label="Save"
          busy={busy}
          className={classes.button}
          pointer="none"
        />
      </div>
    </div>
  );
};

ListingEditForm.propTypes = {
  listing: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  clearAllLocations: PropTypes.func.isRequired,
  copyProviderLocations: PropTypes.func.isRequired,
  back: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  setImageFile: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array
  ]),
};

export default ListingEditForm;
